import { useEffect, useState } from 'react'
import type { WeatherResponse } from '@xingroute/schema'
import { api } from '../api/client.js'
import { useLocale } from '../locale/LocaleContext.js'
import { compassFrom } from '../lib/wind.js'

// WeatherStrip — compact weather display for route detail page.
// Fetches weather for the given coordinate on mount; silently hides on error.

export interface WeatherStripProps {
  lat: number
  lon: number
}

function owmIconUrl(icon: string): string {
  return `https://cdn.jsdelivr.net/npm/qweather-icons/icons/${icon}.svg`
}

function formatTime(dt: number, locale: string): string {
  const d = new Date(dt * 1000)
  if (locale === 'zh') {
    return `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`
  }
  return d.toLocaleTimeString('en', { hour: 'numeric', minute: '2-digit' })
}

export function WeatherStrip({ lat, lon }: WeatherStripProps) {
  const { locale } = useLocale()
  const [weather, setWeather] = useState<WeatherResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setWeather(null)
    setExpanded(false)
    api
      .fetchWeather(lat, lon)
      .then((data) => {
        if (!cancelled) setWeather(data)
      })
      .catch(() => {
        if (!cancelled) setWeather(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [lat, lon])

  if (loading) {
    return (
      <div
        className="flex h-[72px] animate-pulse items-center gap-3 rounded-[var(--xr-radius-md)] bg-[var(--xr-bg-surface-elev)] px-3"
        data-testid="weather-strip-skeleton"
      />
    )
  }

  if (!weather) return null

  const { current, forecast } = weather
  const twelveHourForecast = forecast.slice(0, 6)
  const forecastId = `weather-forecast-${lat.toFixed(4)}-${lon.toFixed(4)}`

  return (
    <div
      className="flex flex-col gap-1.5 rounded-[var(--xr-radius-md)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] px-2.5 py-2"
      data-testid="weather-strip"
    >
      <button
        type="button"
        className="flex w-full items-center gap-2 text-left"
        aria-expanded={expanded}
        aria-controls={twelveHourForecast.length > 0 ? forecastId : undefined}
        aria-label={
          locale === 'zh'
            ? expanded
              ? '收起未来 12 小时天气'
              : '展开未来 12 小时天气'
            : expanded
              ? 'Collapse next 12 hours weather'
              : 'Expand next 12 hours weather'
        }
        onClick={() => setExpanded((value) => !value)}
        data-testid="weather-toggle"
      >
        <img
          src={owmIconUrl(current.icon)}
          alt={current.description}
          width={28}
          height={28}
          className="xr-weather-icon"
        />
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="flex items-center gap-2">
            <span className="text-lg font-semibold leading-none text-[var(--xr-text-primary)]">
              {Math.round(current.temp)}°C
            </span>
            <span className="text-xs leading-none text-[var(--xr-text-muted)]">
              {locale === 'zh' ? '体感' : 'Feels'} {Math.round(current.feels_like)}°
            </span>
            <span className="text-xs leading-none text-[var(--xr-text-secondary)]">
              {current.description}
            </span>
          </span>
          <span className="text-xs leading-none text-[var(--xr-text-muted)]">
            {locale === 'zh' ? '湿度' : 'Humidity'} {current.humidity}%
          </span>
          <span className="text-xs leading-none text-[var(--xr-text-muted)]">
            {locale === 'zh' ? '风' : 'Wind'} {compassFrom(current.wind_deg, locale)}{' '}
            {current.wind_scale
              ? `${current.wind_scale}${locale === 'zh' ? '级' : ''}`
              : `${current.wind_speed.toFixed(1)}m/s`}
          </span>
          {current.rain_1h != null && (
            <span className="text-xs text-[var(--xr-text-muted)]">
              {locale === 'zh' ? '降水' : 'Rain'} {current.rain_1h.toFixed(1)}mm/h
            </span>
          )}
        </div>
        {weather.location_name && (
          <span className="flex-none text-[10px] text-[var(--xr-text-muted)]">
            {weather.location_name}
          </span>
        )}
        {twelveHourForecast.length > 0 && (
          <span className="flex-none text-xs text-[var(--xr-text-muted)]">
            {expanded ? '−' : '+'}
          </span>
        )}
      </button>

      {expanded && twelveHourForecast.length > 0 && (
        <div
          id={forecastId}
          className="grid auto-cols-fr grid-flow-col gap-1 pt-1"
          data-testid="weather-forecast"
        >
          {twelveHourForecast.map((item) => (
            <div
              key={item.dt}
              className="flex flex-col items-center gap-0.5 rounded bg-[var(--xr-bg-surface-elev)] py-1"
            >
              <span className="text-[10px] text-[var(--xr-text-muted)]">
                {formatTime(item.dt, locale)}
              </span>
              <img
                src={owmIconUrl(item.icon)}
                alt={item.description}
                width={20}
                height={20}
                className="xr-weather-icon"
              />
              <span className="text-xs font-medium text-[var(--xr-text-primary)]">
                {Math.round(item.temp)}°
              </span>
              {item.pop > 0.1 && (
                <span className="text-[10px] text-[var(--xr-info)]">
                  {Math.round(item.pop * 100)}%
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
